import React from 'react';
import { Form, FormItem, Input, Radio, ResetButton, Select, SubmitButton } from 'formik-antd';
import { Formik } from 'formik';
import { Col, Row, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { InfoCircleOutlined } from '@ant-design/icons';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { CATEGORY_TEMPLATE_CONTENT_TYPE } from '../../pages/Categories/PageCategories/PageCategories.const';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';
import Yup from '../../../../vendor/yup';
import { Permissions } from '../../../../const/permissions';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import mkTemplatesOptions from '../../utils/mkTemplatesOptions';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import Spinner from '../../../../components/Spinner';



const validationSchema = Yup.object().shape({
  id: Yup.string(),
  client_id: Yup.string(),
  title: Yup.string().max(255, 'Maximum 255 symbols required'),
  template: Yup.string(),
});

const FormCategoriesFilter = ({
  initialValues,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();

  const { data: { data: templates } = { data: [] }, isFetching: isFetchingTemplates } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', CATEGORY_TEMPLATE_CONTENT_TYPE),
  });

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values }) => {
        return (
          <Form layout="vertical">
            <Spinner spinning={isSubmitting}>
              <FormItem
                name='id'
                label={
                  <Row className='w-full' justify="space-between" align="middle">
                    <IntlMessages id="ui-general-id" />
                    <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                      <InfoCircleOutlined className="hp-ml-8" />
                    </Tooltip>
                  </Row>
                }
              >
                <Input
                  name="id"
                  placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
                />
              </FormItem>

              <FormItem
                name='client_id'
                label={
                  <Row className='w-full' justify="space-between" align="middle">
                    <IntlMessages id="ui-general-app-id" />
                    <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                      <InfoCircleOutlined className="hp-ml-8" />
                    </Tooltip>
                  </Row>
                }
              >
                <Input
                  name="client_id"
                  placeholder={intl.formatMessage({ id: 'ui-general-ids-placeholder' })}
                />
              </FormItem>

              <FormItem
                label={intl.formatMessage({ id: 'ui-general-title' })}
                name='title'
              >
                <Input
                  name="title"
                  placeholder={intl.formatMessage({ id: 'ui-general-specify-title' })}
                />
              </FormItem>

              <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-template' })}
                  name='template'
                >
                  <Select
                    allowClear
                    showSearch
                    loading={isFetchingTemplates}
                    name="template"
                    value={values?.template?.toString() ?? null}
                    options={mkTemplatesOptions(templates)}
                    placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                    filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                  />
                </FormItem>
              </PrivateComponent>

              <Row gutter={[ 16, 16 ]} justify='start'>
                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-published' })}
                    name='is_published'
                  >
                    <Radio.Group
                      size='small'
                      name='is_published'
                      defaultValue={null}
                    >
                      <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                      <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                      <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} justify='end'>
                <Col>
                  <ResetButton disabled={false} loading={isSubmitting}>
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-apply" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCategoriesFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default FormCategoriesFilter;
